// src/context/AuthContext.jsx
import React, { createContext, useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import API from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const mountedRef = useRef(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem('token') || null;
    } catch (e) {
      return null;
    }
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Keep API Authorization header in sync (only modify common header)
  useEffect(() => {
    if (token) {
      API.defaults = API.defaults || {};
      API.defaults.headers = API.defaults.headers || {};
      API.defaults.headers.common = API.defaults.headers.common || {};
      API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      try { localStorage.setItem('token', token); } catch (e) { /* ignore */ }
    } else {
      if (API.defaults && API.defaults.headers && API.defaults.headers.common) {
        delete API.defaults.headers.common['Authorization'];
      }
      try { localStorage.removeItem('token'); } catch (e) { /* ignore */ }
    }
  }, [token]);

  // Helper to normalize API response shapes for user object
  const normalizeUserPayload = (resData) => {
    // common shapes:
    // { data: { user: {...}, token: '...' } }
    // { user: {...}, token: '...' }
    // { data: {...} } where data is user
    if (!resData) return null;
    if (resData.user) return resData.user;
    if (resData.data && resData.data.user) return resData.data.user;
    if (resData.data && typeof resData.data === 'object' && !resData.data.token) return resData.data;
    // sometimes the payload is directly user
    if (typeof resData === 'object' && !resData.token) return resData;
    return null;
  };

  const loadUser = useCallback(async () => {
    setLoading(true);
    if (!token) {
      if (mountedRef.current) {
        setUser(null);
        setLoading(false);
      }
      return null;
    }

    try {
      const res = await API.get('/auth/me');
      if (!mountedRef.current) return null;
      const payload = res?.data || null;
      const fetchedUser = normalizeUserPayload(payload);
      if (fetchedUser) {
        setUser(fetchedUser);
        // Optionally persist user locally:
        // try { localStorage.setItem('user', JSON.stringify(fetchedUser)); } catch(e){/*ignore*/ }
        return fetchedUser;
      } else {
        // If server didn't return user, treat as unauthenticated
        setUser(null);
        setToken(null);
        return null;
      }
    } catch (err) {
      console.warn('loadUser error:', err?.response?.data || err.message || err);
      if (mountedRef.current) {
        setUser(null);
        setToken(null);
        try { localStorage.removeItem('token'); } catch (e) {}
      }
      return null;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = useCallback(async (email, password) => {
    try {
      const res = await API.post('/auth/login', { email, password });
      const data = res?.data || {};
      // normalize common shapes
      const tokenValue = data.token || (data.data && data.data.token) || data.accessToken || null;
      const userValue = normalizeUserPayload(data) || normalizeUserPayload(data.data) || null;

      if (tokenValue) setToken(tokenValue);
      if (mountedRef.current && userValue) setUser(userValue);

      return { success: true, token: tokenValue, user: userValue, raw: data };
    } catch (err) {
      const serverMsg = err?.response?.data?.message || err?.response?.data || err?.message || 'Erreur de connexion';
      const error = new Error(typeof serverMsg === 'string' ? serverMsg : JSON.stringify(serverMsg));
      error.response = err?.response;
      throw error;
    }
  }, []);

  const register = useCallback(async (userData) => {
    try {
      const res = await API.post('/auth/register', userData);
      const data = res?.data || {};
      const tokenValue = data.token || (data.data && data.data.token) || null;
      const userValue = normalizeUserPayload(data) || normalizeUserPayload(data.data) || null;
      if (tokenValue) setToken(tokenValue);
      if (mountedRef.current && userValue) setUser(userValue);
      return { success: true, token: tokenValue, user: userValue, raw: data };
    } catch (err) {
      const serverMsg = err?.response?.data?.message || err?.message || 'Erreur d\'inscription';
      const error = new Error(serverMsg);
      error.response = err?.response;
      throw error;
    }
  }, []);

const logout = useCallback(async () => {
  try {

    setUser(null);
    setToken(null);

    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user");
    } catch {}

    if (API?.defaults?.headers?.common) {
      delete API.defaults.headers.common.Authorization;
      delete API.defaults.headers.common.authorization;
    }

  } catch (err) {
    console.warn("logout error:", err);
  }
}, []);


  const value = useMemo(() => ({
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user
  }), [user, token, loading, login, register, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
